import React, { useEffect } from 'react';
import { StyledToastContainer } from '@global/design-system';
import { Provider } from 'react-redux';
import { datadogRum } from '@datadog/browser-rum';

import store from './redux/store';

import Routes from './routes';
import { APP_VERSION, segmentWriteKey, ToastPosition } from '@app/constants';

import { GlobalStyle } from './app.styles';

import useGrowthBookFeatures from '@app/hooks/useGrowthbook';

import { GrowthBookProvider } from '@growthbook/growthbook-react';
import { createClient, AnalyticsProvider } from '@segment/analytics-react';
import AuthProvider from './services/Auth';
import { env } from './env';
import { GlobalProvider as NicheEngageDSGlobalProvider } from '@nicheinc/campus-ds';

export default function App(_props: AppProps) {
	/** GrowthBook setup **/
	const growthbook = useGrowthBookFeatures();

	// explictly have to enable it to avoid local metrics
	// by default case
	if (env.ENABLE_DATADOG === 'on') {
		datadogRum.init({
			applicationId: '3d61d234-172e-4fb7-90b3-df082f74dd6c',
			clientToken: 'pub4dbf744de6ec327d735d607a2cf091af',
			site: 'datadoghq.com',
			service: 'webapp',
			env: env.NODE_ENV,
			// Specify a version number to identify the deployed version of your application in Datadog
			version: APP_VERSION,
			sampleRate: env.NODE_ENV === 'prod' ? 100 : 0,
			trackInteractions: env.NODE_ENV === 'prod',
			trackFrustrations: env.NODE_ENV === 'prod',
			// disabling session replay
			defaultPrivacyLevel: 'mask-user-input',
		});
		// disabling session replay
		if (env.NODE_ENV === 'prod') {
			datadogRum.startSessionReplayRecording();
			/* 
		TODO, 
		when a user has a session, add attributes to the session
		https://docs.datadoghq.com/real_user_monitoring/browser/modifying_data_and_context/?tab=npm
		Some useful attributes to bring in from workspace:
			a. Country
			b. Workspace ID
			c. Company Type
			d. Storyteller ID
			e. IDs from Mixpanel, Keen, Datadog to cross reference events 
			f. Workspace name
		*/
		}
	}

	//segment
	const segmentClient = createClient({
		writeKey: segmentWriteKey || '',
	});

	// Hide the default Zendesk widget button on app load
	useEffect(() => {
		(window as any).zE(function () {
			(window as any).zE('messenger', 'hide');
		});

		// Hide the Zendesk widget when the widget is closed
		(window as any).zE('messenger:on', 'close', () => {
			(window as any).zE('messenger', 'hide');
		});
	}, []);

	return (
		<>
			<GrowthBookProvider growthbook={growthbook}>
				<AnalyticsProvider client={segmentClient}>
					<NicheEngageDSGlobalProvider>
						<GlobalStyle />

						<Provider store={store}>
							<AuthProvider>
								<StyledToastContainer
									hideProgressBar
									style={ToastPosition}
									closeOnClick
								/>
								<Routes />
							</AuthProvider>
						</Provider>
					</NicheEngageDSGlobalProvider>
				</AnalyticsProvider>
			</GrowthBookProvider>
		</>
	);
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
type AppProps = {
	/** This property is used for a personalized greeting  */
	// name: string;
};
